import { MetricType } from '@/constants/metric-type.enum';
import { DeviceType } from '@/constants/deviceTypeEnum';
import { Device } from '@/store/Station/constants';
import { CustomThemeToken } from '@/theme';

import { MetricsNames } from '../MetricsNames';
import Super from '@/comp/Super';
import { DeviceConnectivity } from '@/constants/enums';
import { Trans } from 'react-i18next';

const valuesToHighlight = [
  MetricsNames.Activity,
  MetricsNames.OilTemperature,
  MetricsNames.Power,
  MetricsNames.Flow,
  MetricsNames.Pressure,
  MetricsNames.InverterSpeed,
  MetricsNames.InletValve,
  MetricsNames.BypassValve,
  MetricsNames.WorkingHours,
  MetricsNames.DewPoint,
  MetricsNames.Connectivity,
  MetricsNames.HorsePower,
  MetricType.Activity,
  MetricType.OilTemperatureCelsius,
  MetricType.PowerKw,
  MetricType.FlowM3Min,
  MetricType.PressureBar,
  MetricType.InverterSpeedRPM,
  MetricType.InletValveOpening,
  MetricType.BypassValveOpening,
  MetricType.RunHours,
  MetricType.DewpointTemperatureCelsius,
  MetricType.InletWaterTemperatureCelsius,
  MetricType.OutletWaterTemperatureCelsius,
  MetricType.WaterFlowM3Min,
  MetricType.AirTemperatureCelsius,
  MetricType.MotorWindingTemperatureCelsius,
  MetricType.AirDischargeTemperatureCelsius,
  MetricType.FirstStageTemperatureCelsius,
  MetricType.SecondStageTemperatureCelsius,
  MetricType.ThirdStageTemperatureCelsius,
  MetricType.FourthStageTemperatureCelsius,
  MetricType.SurgeCount,
  MetricType.VibrationsLimitMicrons,
  MetricType.FirstStageVibrationsMicrons,
  MetricType.SecondStageVibrationsMicrons,
  MetricType.ThirdStageVibrationsMicrons,
  MetricType.FourthStageVibrationsMicrons,
  MetricType.VoltageVolt,
  MetricType.PowerFactor,
  MetricType.IntercoolerPressureBar,
  MetricType.InteriorAmbientTemperatureCelsius,
  MetricType.ExteriorAmbientTemperatureCelsius,
  MetricType.WaterPressureIn,
  MetricType.WaterPressureOut,
  MetricType.WaterPressureDelta,
  MetricType.FrequencyHz,
  MetricType.BlowerSpeedRPM,
  MetricType.DissolvedOxygenPPM,
];

export const isValueHighlighted = (itemName: string): boolean => {
  return valuesToHighlight.includes(itemName);
};

export const getOnDeviceSensorsToDisplay = (onDevices: Device[], deviceTypes: DeviceType[]) => {
  const sortedOnDevices = onDevices.slice()?.sort((d1, d2) => d1.elementIndex - d2.elementIndex);
  return deviceTypes.map(deviceType => sortedOnDevices?.filter(device => device?.deviceType === deviceType));
};

export const deviceIsOfType = (deviceType: DeviceType, deviceTypes: DeviceType[]) => {
  return deviceTypes.includes(deviceType);
};

export const isSelectedNodeOnMap = (deviceId: string, selectedDeviceId: string, renderedOnMap: boolean) =>
  deviceId === selectedDeviceId && renderedOnMap;

export const getWidgetFillColor = (isSelected: boolean, token: CustomThemeToken, renderedOnMap?: boolean) => {
  if (renderedOnMap === false) return token.colorBgElevated;
  return isSelected ? token.Ecoplant_Compressor.colorBGSelected : token.colorBgContainer;
};

export const getWidgetSecondaryFillColor = (isSelected: boolean, token: CustomThemeToken) =>
  isSelected ? token.Ecoplant_Compressor.colorBGSelected : token.colorBorder;

export const getWidgetBorderColor = (
  isSelected: boolean,
  inMaintenance: boolean,
  renderedOnMap: boolean,
  token: CustomThemeToken,
) => {
  if (inMaintenance && renderedOnMap) return token.Ecoplant_Compressor.colorBGMaintenance;

  return getWidgetSecondaryFillColor(isSelected, token);
};

export interface Sensor {
  name: string;
  pressureBar?: number;
  flowM3Min?: number;
  dissolvedOxygenPPM?: number;
  connectivity: DeviceConnectivity;
}

export const aerationBasinOnDevicesTypes = [
  DeviceType.WaterTemperatureSensor,
  DeviceType.PhSensor,
  DeviceType.AmmoniaAmmoniumSensor,
  DeviceType.NitrateSensor,
  DeviceType.NitriteSensor,
  DeviceType.OxidationReductionPotentialSensor,
];

export const sensorTypeMapping = {
  pressure: { value: (sensor, tvalue) => tvalue(sensor.pressureBar, 'bar'), unit: (tunit, t) => t(tunit('Bar')) },
  flow: {
    value: (sensor, tvalue) => tvalue(sensor?.flowM3Min, 'm^3/min'),
    unit: tunit => (
      <Trans i18nKey={tunit('m3/min')}>
        <Super />
      </Trans>
    ),
  },
  waterTemperature: {
    value: (sensor, tvalue) => tvalue(sensor.waterTemperatureCelsius, '°C'),
    unit: (tunit, t) => t(tunit('°C')),
  },
  ph: {
    value: (sensor, tvalue) => tvalue(sensor.Ph),
    unit: () => null,
  },
  ammoniaAmmonium: {
    value: (sensor, tvalue) => tvalue(sensor.ammoniaAmmoniumPPM, 'ppm'),
    unit: (tunit, t) => t(tunit('ppm')),
  },
  nitrateNO3: {
    value: (sensor, tvalue) => tvalue(sensor.nitratePPM, 'ppm'),
    unit: (tunit, t) => t(tunit('ppm')),
  },
  nitriteNO2: {
    value: (sensor, tvalue) => tvalue(sensor.nitritePPM, 'ppm'),
    unit: (tunit, t) => t(tunit('ppm')),
  },
  oxidationReductionPotential: {
    value: (sensor, tvalue) => tvalue(sensor.oxidationReductionPotentialMV, 'mV'),
    unit: (tunit, t) => t(tunit('mV')),
  },
};

export const createLoadingProgressTimer = (
  onProgressUpdate: (progress: number) => void,
  totalDuration = 1500,
  updateInterval = 50,
): NodeJS.Timeout => {
  const progressIncrement = (updateInterval / totalDuration) * 100;
  let currentProgress = 0;

  const progressTimer = setInterval(() => {
    currentProgress += progressIncrement;
    if (currentProgress >= 100) {
      currentProgress = 100;
      clearInterval(progressTimer);
    }
    onProgressUpdate(currentProgress);
  }, updateInterval);

  return progressTimer;
};
